// The arrow that says what something on the chain is pointing at.
//
// Pure screen geometry, in pixels, so it can be tested without a GPU: given
// two points it returns a curve and a head, and `board.ts` normalises the
// result against the canvas.
//
// Pixels in and pixels out is deliberate. The overlay SVG is stretched to the
// canvas with `preserveAspectRatio="none"`, so anything shaped in *its*
// coordinate space is skewed by whatever the window's aspect ratio happens to
// be -- an arrowhead comes out wide and flat on a laptop and tall and thin on
// a phone. Shaping in pixels and converting once at the end is what keeps the
// head looking like a head.

export type Point = [number, number];

export interface Arrow {
  /** The curve, flattened into points. */
  shaft: Point[];
  /** The head, as a triangle whose first point is the tip. */
  head: Point[];
}

/** Below this the two cards are effectively on top of each other. */
const MIN_LENGTH = 12;
/** How far short of the target's centre the head stops. */
const STOP = 26;
const HEAD = 13;
const STEPS = 14;

/**
 * A curved arrow from one point to another.
 *
 * `bow` pushes the curve off to one side, so several arrows leaving the same
 * card fan out instead of stacking: two arrows on the same curve are one arrow
 * as far as the eye is concerned, which is the opposite of the point.
 *
 * Returns null when there is no sensible direction to draw -- normalising a
 * zero-length vector is how a NaN reaches the DOM, and an SVG with NaN in a
 * point list silently drops the whole shape.
 */
export function aimArrow(from: Point, to: Point, bow: number): Arrow | null {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (!Number.isFinite(len) || len < MIN_LENGTH) return null;

  const mid: Point = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
  const nx = -dy / len;
  const ny = dx / len;
  const ctrl: Point = [mid[0] + nx * bow, mid[1] + ny * bow];

  const at = (t: number): Point => [
    (1 - t) * (1 - t) * from[0] + 2 * (1 - t) * t * ctrl[0] + t * t * to[0],
    (1 - t) * (1 - t) * from[1] + 2 * (1 - t) * t * ctrl[1] + t * t * to[1],
  ];

  // Walk back from the end until the curve is clear of the target's centre, so
  // the head points *at* the card rather than sitting on its artwork. Measured
  // along the curve rather than by shortening the straight line, because a
  // bowed curve arrives from a different direction than the chord.
  // Starts at "not found". It used to start at 1, which meant that when the
  // whole curve lay within STOP of the target -- two cards close together on
  // screen -- the loop simply never fired and the arrow was drawn to the
  // target's exact centre, head sitting on the artwork it was pointing at.
  // The failure looked like a design decision rather than a bug, which is why
  // it is worth a test.
  let end = -1;
  for (let t = 1; t > 0; t -= 0.02) {
    if (Math.hypot(to[0] - at(t)[0], to[1] - at(t)[1]) >= STOP) { end = t; break; }
  }
  // Nothing to draw: the cards are close enough that which one is meant is not
  // in doubt anyway.
  if (end <= 0.02) return null;

  const shaft: Point[] = [];
  for (let i = 0; i <= STEPS; i++) shaft.push(at((i / STEPS) * end));

  const tip = at(end);
  const before = at(Math.max(0, end - 0.06));
  const tl = Math.hypot(tip[0] - before[0], tip[1] - before[1]) || 1;
  const ux = (tip[0] - before[0]) / tl;
  const uy = (tip[1] - before[1]) / tl;
  const head: Point[] = [
    tip,
    [tip[0] - ux * HEAD + uy * HEAD * 0.5, tip[1] - uy * HEAD - ux * HEAD * 0.5],
    [tip[0] - ux * HEAD - uy * HEAD * 0.5, tip[1] - uy * HEAD + ux * HEAD * 0.5],
  ];

  return { shaft, head };
}


// -- arriving and leaving -----------------------------------------------------
//
// A shape that switches on reads as the interface changing state; one that
// grows into place reads as something happening on the table. These are the
// three pieces of that, kept here rather than in the HUD because they are
// geometry, and geometry is worth testing.

/**
 * The first `t` of a flattened curve, so a line can draw itself in.
 *
 * Measured in points rather than in arc length: the curve is flattened into
 * evenly spaced samples, so the two agree to within a pixel and the cheap
 * version is indistinguishable in motion.
 */
export function drawnTo(points: Point[], t: number): Point[] {
  if (t >= 1) return points;
  const last = (points.length - 1) * Math.max(0, t);
  const whole = Math.floor(last);
  const out = points.slice(0, whole + 1);
  const a = points[whole];
  const b = points[whole + 1];
  const f = last - whole;
  // Only when it is actually part way along. At f = 0 the interpolated point
  // *is* the one already in the list, and a two-point polyline whose points
  // coincide is not nothing: with a round linecap the browser paints a dot,
  // so the first frame of every arrow put a bead on the caster.
  if (a && b && f > 1e-6) {
    out.push([a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f]);
  }
  return out;
}

/**
 * An arrowhead grown from its own tip, so it lands rather than appears.
 *
 * Scaled about the tip and not the centroid: the tip is the thing that has to
 * stay on the target while the rest of the head catches up.
 */
export function headPoints(head: Point[], t: number): string {
  const tip = head[0]!;
  return head
    .map(([x, y]) => `${tip[0] + (x - tip[0]) * t},${tip[1] + (y - tip[1]) * t}`)
    .join(' ');
}

/** A quad scaled about its own centre. */
export function scaledQuad(points: Point[], t: number): Point[] {
  const cx = points.reduce((n, p) => n + p[0], 0) / points.length;
  const cy = points.reduce((n, p) => n + p[1], 0) / points.length;
  return points.map(([x, y]) => [cx + (x - cx) * t, cy + (y - cy) * t]);
}
